import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Bot, ChevronDown, ChevronUp, Copy, CreditCard, Eye, FileText, Flame, Gauge,
  Globe2, Languages, Link2, Mail, Megaphone, Monitor, MousePointerClick, Minus, PackageOpen, QrCode, Repeat2, ScrollText, Search,
  Share2, ShoppingBag, ShoppingCart, Smartphone, Sparkles, Store, Tablet, TicketPercent, Type, Undo2, Wallet, XCircle,
} from 'lucide-react';

import { loadStorefrontSites, loadStorefrontView } from './api';
import { useXomicsAuth } from './AuthContext';
import { Empty, Failed, Head, Loading } from './parts';
import { hasTx, tx } from './i18n';
import { errorMessage } from './ui';
import { COMPACT, DECIMAL, FilterMenu, NUMBER, RangeTabs, Share, counted } from './MobileParts';
import './storefront.css';

// What every page of the E-commerce section's storefront analytics shares: its
// words, money, changes against the previous period, channels, devices, event
// names, the funnel and the table. Drawn with the Mobile app section's parts.

export const st = (key, values) => tx(`ui.storefront.${key}`, values);
export const stCount = (key, count, values) => counted(`ui.storefront.${key}`, count, values);

export const DAY_MS = 86400000;

// ------------------------------------------------------------------ numbers

const moneyFormats = new Map();

function moneyFormat(currency, compact) {
  const key = `${currency}:${compact}`;
  if (!moneyFormats.has(key)) {
    try {
      // The symbol, not the narrow one: "kr" alone could be any of three krona.
      const format = new Intl.NumberFormat(undefined, {
        style: 'currency', currency, ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
      });
      moneyFormats.set(key, { format, digits: new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits });
    } catch {
      moneyFormats.set(key, { format: null, digits: 2 });
    }
  }
  return moneyFormats.get(key);
}

// An amount in its currency's minor unit, as the store and the orders keep it.
// The server's `minorUnit` wins over the browser's idea of the currency.
export function money(minor, currency, { compact = false, minorUnit = null } = {}) {
  if (minor === null || minor === undefined || !currency) return st('none');
  const { format, digits } = moneyFormat(currency, compact);
  const amount = Number(minor) / (10 ** (Number.isInteger(minorUnit) ? minorUnit : digits));
  return format ? format.format(amount) : `${currency} ${NUMBER.format(amount)}`;
}

// A Money entry, a spend or an order total, with the minor unit it came with.
export function moneyOf(entry, key = 'netMinor', options = {}) {
  if (!entry) return st('no_revenue');
  return money(entry[key], entry.currency, { ...options, minorUnit: entry.minorUnit });
}

export function count(value) {
  return value === null || value === undefined ? st('none') : NUMBER.format(value);
}

export function compact(value) {
  return value === null || value === undefined ? st('none') : COMPACT.format(value);
}

// A share as the section writes it: whole percents from ten up, one decimal under.
export function rate(part, whole) {
  if (!whole) return '0%';
  const value = (part / whole) * 100;
  return `${value >= 10 || value === 0 ? Math.round(value) : value.toFixed(1)}%`;
}

// A ratio the server already worked out (0 to 1).
export function ratio(value) {
  if (value === null || value === undefined) return st('none');
  const percentValue = value * 100;
  return `${percentValue >= 10 || percentValue === 0 ? Math.round(percentValue) : percentValue.toFixed(1)}%`;
}

// How long something took, in the viewer's units; "none" when nothing was measured.
export function duration(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return st('none');
  if (ms < 1000) return `${Math.round(ms)} ms`;
  // Under ten seconds a tenth still matters: a page that loads in 2.4 s is not a 2 s page.
  if (ms < 9950) return st('seconds', { count: DECIMAL.format(Math.round(ms / 100) / 10) });
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return st('seconds', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return st('minutes_seconds', { minutes, seconds: String(seconds % 60).padStart(2, '0') });
  return st('hours_minutes', { hours: Math.floor(minutes / 60), minutes: String(minutes % 60).padStart(2, '0') });
}

export function clock(at) {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function dateTime(at) {
  if (at === null || at === undefined) return st('none');
  return new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function shortDate(at) {
  if (at === null || at === undefined) return st('none');
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// How a value moved against the period before, as { text, tone }: "+12%" and good
// or bad by which way is better for it. Nothing when there was nothing before.
export function change(current, previous, better = 'up') {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  if (previous === 0) return current === 0 ? { text: '0%', tone: 'flat', direction: 'flat' } : { text: st('new_in_period'), tone: better === 'up' ? 'good' : 'bad', direction: 'up' };
  const value = ((current - previous) / previous) * 100;
  if (Math.abs(value) < 0.5) return { text: '0%', tone: 'flat', direction: 'flat' };
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  const direction = rounded > 0 ? 'up' : 'down';
  return {
    text: `${rounded > 0 ? '+' : '−'}${NUMBER.format(Math.abs(rounded))}%`,
    tone: direction === better ? 'good' : 'bad',
    direction,
  };
}

// The change as a small mark: an arrow, the percent, green when it went the good way.
export function Delta({ current, previous, better = 'up', label = null }) {
  const moved = change(current, previous, better);
  if (!moved) return null;
  const Icon = moved.direction === 'up' ? ArrowUpRight : moved.direction === 'down' ? ArrowDownRight : Minus;
  return (
    <span className={`xo-sf-delta is-${moved.tone}`} title={label || st('versus_previous')}>
      <Icon aria-hidden="true" />{moved.text}
    </span>
  );
}

// A headline number's detail line: its change, then what it was before.
export function kpiDetail(current, previous, { better = 'up', format = count, extra = null } = {}) {
  return (
    <span className="xo-sf-kpi-detail">
      <Delta current={current} previous={previous} better={better} />
      <span>{extra || (previous === null || previous === undefined ? st('no_previous') : st('previous_value', { value: format(previous) }))}</span>
    </span>
  );
}

// ------------------------------------------------------------------ names

const displayNames = new Map();
function displayName(type, code) {
  if (!code) return null;
  try {
    if (!displayNames.has(type)) displayNames.set(type, new Intl.DisplayNames(undefined, { type }));
    return displayNames.get(type).of(code) || code;
  } catch {
    return code;
  }
}
// A language or a currency by its name in the reader's language: "en" reads "English", "SEK" "Swedish Krona".
export const languageName = (code) => displayName('language', code) || st('none');
export const currencyName = (code) => displayName('currency', code) || st('none');

// A code the catalogue does not name yet, in words: ADDRESS_NOT_VERIFIED reads "Address not verified".
export function inWords(code) {
  const words = String(code).replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : String(code);
}

// A label from the catalogue when it has one, otherwise the code in words.
function known(prefix, code) {
  if (code === null || code === undefined || code === '') return st('none');
  const key = `ui.storefront.${prefix}.${String(code).toLowerCase()}`;
  return hasTx(key) ? tx(key) : inWords(code);
}

// A payment status, a checkout step or a vital's rating as the catalogue names it.
export const codeText = known;

const CHANNEL_ICONS = {
  paid: Megaphone, social: Share2, email: Mail, organic: Search, direct: Link2, referral: Globe2, campaign: QrCode,
};

export const channelLabel = (channel) => known('channel', channel);

// The ad networks behind the click ids a landing can carry, by the id's own name.
const CLICK_ID_NAMES = { gclid: 'Google Ads', fbclid: 'Meta', ttclid: 'TikTok', msclkid: 'Microsoft Ads', li_fat_id: 'LinkedIn' };
export const clickIdName = (type) => CLICK_ID_NAMES[type] || type;

export function ChannelMark({ channel, withLabel = true }) {
  const Icon = CHANNEL_ICONS[String(channel || '').toLowerCase()] || Globe2;
  return (
    <span className={`xo-sf-channel is-${String(channel || 'unknown').toLowerCase()}`} title={channelLabel(channel)}>
      <Icon aria-hidden="true" />{withLabel && <span>{channelLabel(channel)}</span>}
    </span>
  );
}

const DEVICE_ICONS = { desktop: Monitor, tablet: Tablet, mobile: Smartphone, phone: Smartphone, bot: Bot };

export const deviceLabel = (device) => known('device', device);

export function DeviceMark({ device, withLabel = false }) {
  const Icon = DEVICE_ICONS[String(device || '').toLowerCase()] || Monitor;
  return (
    <span className="xo-sf-device" title={deviceLabel(device)}>
      <Icon aria-hidden="true" />{withLabel && <span>{deviceLabel(device)}</span>}
    </span>
  );
}

const EVENT_ICONS = {
  page_view: FileText, page_leave: FileText, scroll: ScrollText, click: MousePointerClick, rage_click: Flame, dead_click: XCircle,
  form: Type, element_visible: Eye, product_impression: Eye, product_view: PackageOpen, variant_select: Repeat2,
  bag_add: ShoppingBag, bag_remove: ShoppingBag, bag_view: ShoppingCart, coupon_attempt: TicketPercent, checkout_step: CreditCard,
  checkout_error: AlertTriangle, payment_started: Wallet, payment_failed: AlertTriangle, purchase: Sparkles,
  market_change: Globe2, currency_change: Globe2, language_change: Languages, search: Search, tracking_view: PackageOpen,
  error: AlertTriangle, web_vital: Gauge, visibility: Eye, copy: Copy, custom: Sparkles, back: Undo2,
};

export const eventLabel = (type) => known('event_type', type);

export function EventIcon({ type }) {
  const Icon = EVENT_ICONS[type] || Sparkles;
  return <Icon aria-hidden="true" />;
}

// Commerce and failure events stand out on a timeline; the rest stay quiet.
// So does a Back, which is often how a visitor leaves a step of the purchase.
export function eventTone(type) {
  if (type === 'purchase') return 'good';
  if (['bag_add', 'bag_view', 'checkout_step', 'payment_started', 'coupon_attempt', 'product_view', 'back'].includes(type)) return 'commerce';
  if (['error', 'checkout_error', 'payment_failed', 'rage_click', 'dead_click'].includes(type)) return 'bad';
  return 'flat';
}

// A visitor has no name: the store never asks. The day's visitor number stands in.
export function visitorName(visitor) {
  const tag = String(visitor || '').replace(/[^a-z0-9]/gi, '').slice(0, 4).toUpperCase();
  return tag ? st('visitor_tag', { tag }) : st('visitor');
}

// An element as one row across languages: the words it showed, else its track
// name, selector or tag. Its translation key stays the row's identity; naming a
// key in words is the server's job.
export function elementName(element) {
  return element.text || element.track || element.selector || element.tag || st('none');
}

// What was clicked as a visitor saw it: its text, else its tracking name in
// words, else what kind of thing it was. Never the page's code.
const ELEMENT_KINDS = { a: 'link', button: 'button', img: 'image', input: 'field', select: 'field', textarea: 'field' };
export function clickedName(element) {
  if (element.text) return element.text;
  if (element.ariaLabel) return element.ariaLabel;
  if (element.track) return inWords(element.track);
  return st(`event.kind_${ELEMENT_KINDS[element.tag] || 'other'}`);
}

// ------------------------------------------------------------------ names

// Page titles and product names, read once per site, so that a page is named by
// its title and a product by its name wherever an answer carries only a path or
// a code. Until they arrive, or when they cannot be read, the path or code shows.
const NO_NAMES = { pages: new Map(), products: new Map() };
const namesBySite = new Map();
const NamesContext = createContext(NO_NAMES);

function readNames(siteCode) {
  if (!namesBySite.has(siteCode)) {
    namesBySite.set(siteCode, Promise.all([
      loadStorefrontView('pages', { site: siteCode, days: 30 }),
      loadStorefrontView('products', { site: siteCode, days: 30 }),
    ]).then(([pages, products]) => ({
      pages: new Map(pages.pages.filter((page) => page.title).map((page) => [page.path, page.title])),
      products: new Map(products.products.filter((product) => product.name).map((product) => [product.productCode, product.name])),
    })).catch(() => { namesBySite.delete(siteCode); return NO_NAMES; }));
  }
  return namesBySite.get(siteCode);
}

function NamesProvider({ site, children }) {
  const [names, setNames] = useState(NO_NAMES);
  useEffect(() => {
    let live = true;
    readNames(site.code).then((read) => { if (live) setNames(read); });
    return () => { live = false; };
  }, [site.code]);
  return <NamesContext.Provider value={names}>{children}</NamesContext.Provider>;
}

// `page(path)` is the page's title, or null; `product(code)` its name, or the code.
export function useStoreNames() {
  const names = useContext(NamesContext);
  return useMemo(() => ({
    page: (path) => names.pages.get(path || '/') || null,
    pageOrPath: (path) => names.pages.get(path || '/') || path || '/',
    product: (code) => (code ? names.products.get(code) || code : ''),
  }), [names]);
}

// A page by its title, with its address small beside it; the address alone when it has no title.
export function PathText({ path, title = null }) {
  const names = useStoreNames();
  const name = title || names.page(path);
  return (
    <span className={`xo-sf-path${name ? '' : ' is-bare'}`} title={path || '/'}>
      {name && <strong>{name}</strong>}<code>{path || '/'}</code>
    </span>
  );
}

// Revenue arrives as one entry per currency and is never converted, so amounts
// in different currencies are never compared: the server orders them by orders,
// then currency code. The first carries the headline number; the others are
// named beside it.
function byOrders(list) {
  return [...list].sort((a, b) => b.orders - a.orders || a.currency.localeCompare(b.currency));
}

export function primaryMoney(list) {
  return Array.isArray(list) && list.length > 0 ? list[0] : null;
}

export function MoneyText({ list, compact: short = false }) {
  const first = primaryMoney(list);
  if (!first) return <span className="xo-sf-money is-none">{st('no_revenue')}</span>;
  const rest = list.slice(1);
  return (
    <span className="xo-sf-money" title={list.map((entry) => moneyOf(entry)).join(' · ')}>
      {moneyOf(first, 'netMinor', { compact: short })}
      {rest.length > 0 && <small>{stCount('more_currencies', rest.length)}</small>}
    </span>
  );
}

// Every currency after the headline one, for a detail line.
export function otherMoney(list) {
  return Array.isArray(list) ? list.slice(1) : [];
}

// Revenue lists added up per currency, never converted, in the server's order.
export function addMoney(lists) {
  const byCurrency = new Map();
  for (const list of lists) {
    for (const entry of list) {
      const sum = byCurrency.get(entry.currency) || { ...entry, orders: 0, grossMinor: 0, refundedMinor: 0, netMinor: 0 };
      sum.orders += entry.orders;
      sum.grossMinor += entry.grossMinor;
      sum.refundedMinor += entry.refundedMinor;
      sum.netMinor += entry.netMinor;
      byCurrency.set(entry.currency, sum);
    }
  }
  return byOrders([...byCurrency.values()]);
}

// The same currency's figure in the period before, for a change mark.
export function previousMoney(list, currency) {
  if (!Array.isArray(list)) return null;
  const found = list.find((entry) => entry.currency === currency);
  return found ? found.netMinor : 0;
}

// ------------------------------------------------------------------ site and access

let sitesPromise = null;

// The sites the store analytics know, read once per visit to the portal.
function readSites() {
  if (!sitesPromise) sitesPromise = loadStorefrontSites().catch((error) => { sitesPromise = null; throw error; });
  return sitesPromise;
}

/**
 * The site a page reads: the one named in ?site=, otherwise the first active
 * one. Today there is one store; a picker appears once there are more.
 */
export function useStorefrontSite() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState({ sites: null, error: null });
  const load = useCallback(() => {
    setState({ sites: null, error: null });
    readSites()
      .then((sites) => setState({ sites, error: null }))
      .catch((error) => setState({ sites: null, error: errorMessage(error) }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const wanted = searchParams.get('site');
  const sites = state.sites || [];
  const site = sites.find((entry) => entry.code === wanted) || sites.find((entry) => entry.isActive) || sites[0] || null;
  const choose = useCallback((code) => {
    const next = new URLSearchParams(searchParams);
    next.set('site', code);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  return { site, sites, loading: state.sites === null && state.error === null, error: state.error, retry: load, choose };
}

export function useStorefrontAccess() {
  const { permissions } = useXomicsAuth();
  return {
    campaigns: permissions.includes('STOREFRONT_CAMPAIGNS_MANAGE'),
    settings: permissions.includes('STOREFRONT_SETTINGS_MANAGE'),
  };
}

// Waits for the site, then draws the page with it. Nothing to show when no site exists yet.
export function StorefrontGate({ store, children }) {
  if (store.loading) return <Loading label={st('reading_sites')} />;
  if (store.error) return <Failed message={store.error} onRetry={store.retry} />;
  if (!store.site) return <Empty title={st('no_site')} detail={st('no_site_detail')} />;
  return <NamesProvider site={store.site}>{children(store.site)}</NamesProvider>;
}

function SitePicker({ store }) {
  if (store.sites.length < 2) return null;
  return (
    <FilterMenu icon={Store} label={st('site')} value={store.site.code} onChange={store.choose}
      options={store.sites.map((entry) => ({ value: entry.code, label: entry.name }))} />
  );
}

// ------------------------------------------------------------------ page frame

// A page's own action (a button) goes last, right of the site and the period.
export function StorefrontHead({ title, subtitle, days, onDays, store = null, children }) {
  return (
    <Head title={title} subtitle={subtitle}>
      {store && <SitePicker store={store} />}
      {onDays && <RangeTabs value={days} onChange={onDays} />}
      {children}
    </Head>
  );
}

// A page's state when its read has not come back yet, or failed.
export function ReadState({ resource, label }) {
  if (resource.loading && resource.data === null) return <Loading label={label} />;
  if (resource.error && resource.data === null) return <Failed message={resource.error} onRetry={resource.refresh} />;
  return null;
}

// ------------------------------------------------------------------ funnel

// Steps side by side, each as tall as its share of the first, with how many went
// on from the step before. Narrow screens stack them.
export function Funnel({ steps, compact: small = false }) {
  const first = steps.length > 0 ? steps[0].count : 0;
  return (
    <ol className={`xo-sf-funnel${small ? ' is-compact' : ''}`} style={{ '--xo-sf-steps': steps.length }}>
      {steps.map((step, index) => {
        const previous = index === 0 ? null : steps[index - 1].count;
        const share = first > 0 ? step.count / first : 0;
        const size = `${Math.max(step.count > 0 ? 2 : 0, share * 100)}%`;
        return (
          <li key={step.key}>
            <span className="xo-sf-funnel-name">{step.label}</span>
            <strong className="xo-sf-funnel-count">{count(step.count)}</strong>
            <span className="xo-sf-funnel-bar" aria-hidden="true"><i style={{ height: size, '--w': size }} /></span>
            <span className="xo-sf-funnel-share">{index === 0 ? st('funnel.start') : st('funnel.of_first', { share: rate(step.count, first) })}</span>
            {previous !== null && (
              <span className={`xo-sf-funnel-step${previous > 0 && step.count / previous < 0.25 ? ' is-steep' : ''}`}>
                {st('funnel.continued', { share: rate(step.count, previous) })}
              </span>
            )}
            {step.note && <small className="xo-sf-funnel-note">{step.note}</small>}
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------------------ table

function sortValue(row, column) {
  const value = column.sortValue ? column.sortValue(row) : row[column.key];
  return value === null || value === undefined ? -Infinity : value;
}

/**
 * A table that turns into one card per row on a phone, each value under its
 * column's name. `columns` are { key, label, render(row), align: 'end',
 * sortValue(row), wide } (a wide column is left out on narrow screens).
 */
export function StoreTable({ columns, rows, rowKey, onRow = null, active = null, initialSort = null, empty = null, limit = null, className = '' }) {
  const [sort, setSort] = useState(initialSort);
  const [showAll, setShowAll] = useState(false);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((entry) => entry.key === sort.key);
    if (!column) return rows;
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = sortValue(a, column);
      const right = sortValue(b, column);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [rows, columns, sort]);
  if (rows.length === 0) return empty;
  const shown = limit !== null && !showAll ? sorted.slice(0, limit) : sorted;
  return (
    <div className={`xo-sf-table-wrap ${className}`}>
      <table className="xo-sf-table">
        <thead>
          <tr>
            {columns.map((column) => {
              const sorting = sort && sort.key === column.key;
              const sortable = column.sortable !== false && column.render !== undefined;
              return (
                <th key={column.key} className={`${column.align === 'end' ? 'is-end' : ''}${column.wide ? ' is-wide' : ''}`}
                  aria-sort={sorting ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined} scope="col">
                  {sortable ? (
                    <button type="button" onClick={() => setSort(sorting && sort.direction === 'desc'
                      ? { key: column.key, direction: 'asc' } : { key: column.key, direction: 'desc' })}>
                      {column.label}
                      {sorting && (sort.direction === 'asc' ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />)}
                    </button>
                  ) : column.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const key = rowKey(row);
            return (
              <tr key={key} className={`${onRow ? 'is-clickable' : ''}${active === key ? ' is-active' : ''}`}
                onClick={onRow ? () => onRow(row) : undefined}
                onKeyDown={onRow ? (event) => { if (event.key === 'Enter') onRow(row); } : undefined}
                tabIndex={onRow ? 0 : undefined}>
                {columns.map((column) => (
                  <td key={column.key} data-label={column.label}
                    className={`${column.align === 'end' ? 'is-end' : ''}${column.wide ? ' is-wide' : ''}${column.primary ? ' is-primary' : ''}`}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {limit !== null && sorted.length > limit && (
        <button type="button" className="xo-mob-more" onClick={() => setShowAll(!showAll)}>
          {showAll ? st('show_fewer') : st('show_all', { count: NUMBER.format(sorted.length) })}
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ shares

// Six navy steps, darkest first, for parts of one whole.
const SHARE_SHADES = ['#0a1e57', '#203a85', '#4b64a8', '#7185bd', '#a9b5d9', '#d3d9ec'];

// Parts of one whole as one bar, the largest first, the rest together at the end;
// a part wide enough carries its name and share.
export function ShareStrip({ parts, total }) {
  if (!(total > 0)) return null;
  const sorted = [...parts].filter((part) => part.value > 0).sort((a, b) => b.value - a.value);
  const shown = sorted.slice(0, 5);
  const rest = total - shown.reduce((sum, part) => sum + part.value, 0);
  const all = rest > 0 ? [...shown, { key: 'rest', label: st('share_rest'), value: rest }] : shown;
  return (
    <div className="xo-sf-strip" role="img" aria-label={all.map((part) => `${part.label} ${rate(part.value, total)}`).join(', ')}>
      {all.map((part, index) => (
        <span key={part.key} className={index > 2 ? 'is-light' : ''} title={`${part.label}: ${rate(part.value, total)}`}
          style={{ flexGrow: part.value, background: SHARE_SHADES[Math.min(index, SHARE_SHADES.length - 1)] }}>
          {part.value / total >= 0.3 && <b>{part.label}</b>}
          {part.value / total >= 0.12 && <em>{rate(part.value, total)}</em>}
        </span>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ weekdays

const WEEKDAY_BASE = Date.UTC(2024, 0, 1); // A Monday.
const weekdayName = (index) => new Date(WEEKDAY_BASE + index * DAY_MS).toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' });

// Visits per day of the week from a weekday by hour grid, as bars, with the busiest day and hour named.
export function WeekdayBars({ grid }) {
  const days = grid.map((row) => row.reduce((sum, value) => sum + value, 0));
  if (days.every((value) => value === 0)) return null;
  const hours = Array.from({ length: 24 }, (_, hour) => grid.reduce((sum, row) => sum + (row[hour] || 0), 0));
  const most = Math.max(1, ...days);
  const busiestDay = days.indexOf(Math.max(...days));
  const busiestHour = hours.indexOf(Math.max(...hours));
  return (
    <div className="xo-sf-weekdays">
      <h3 className="xo-mob-subhead">{st('when.by_day')}</h3>
      <ol>
        {days.map((value, index) => (
          <li key={index} className={index === busiestDay ? 'is-top' : ''}>
            <span>{weekdayName(index)}</span>
            <Share value={value} total={most} />
            <em>{NUMBER.format(value)}</em>
          </li>
        ))}
      </ol>
      <p className="xo-mob-footnote">{st('when.busiest', { day: weekdayName(busiestDay), hour: String(busiestHour).padStart(2, '0') })}</p>
    </div>
  );
}

// A ranked list row's bar and number, for the lists that are not tables.
export function RankRows({ rows, empty = null }) {
  if (rows.length === 0) return empty;
  const most = Math.max(1, ...rows.map((row) => row.value));
  return (
    <ol className="xo-mob-rank xo-sf-rank">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="xo-mob-rank-icon">{row.icon}</span>
          <span className="xo-mob-rank-main"><strong title={row.title || undefined}>{row.label}</strong>{row.detail && <small>{row.detail}</small>}</span>
          <span className="xo-mob-share" aria-hidden="true"><i style={{ width: `${Math.max(row.value > 0 ? 2 : 0, (row.value / most) * 100)}%` }} /></span>
          <em>{row.display === undefined ? NUMBER.format(row.value) : row.display}</em>
        </li>
      ))}
    </ol>
  );
}
